import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Check, Landmark, Sparkles, Building2, ShieldCheck, ArrowRight, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import CompanyCombobox from "@/components/CompanyCombobox";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TIERS = [
  { key: "public", name: "Public", price: "Free", period: "", who: "Everyone", cta: "Browse supervisors", to: "/supervisors",
    features: ["Threshold-protected supervisor and company scores", "8-trait breakdowns and quarterly trends", "Anonymous reviews, check-ins and trajectory", "Documentation Vault for every employee"] },
  { key: "claimed", name: "Claimed Profile", price: "$149", period: "/month", who: "Small & mid-size companies", cta: "Claim your company", checkout: true, highlight: true,
    features: ["Verified employer badge on your company page", "Respond publicly to reviews of your managers", "Branded company profile", "Monthly email summary of new signal"] },
  { key: "insights", name: "Insights", price: "$8", period: "/employee/yr", note: "minimum $2,400/yr", who: "HR & People teams", cta: "Request a demo", to: "/for-employers#demo-form",
    features: ["Per-manager 8-trait trends and Open to Feedback score", "Department and industry benchmarking", "Trajectory and threshold alerts", "Quarterly leadership PDF report", "Everything in Claimed Profile"] },
  { key: "enterprise", name: "Enterprise", price: "Custom", period: "", who: "Large orgs & agencies", cta: "Talk to us", to: "/for-employers#demo-form",
    features: ["SSO and multi-site / multi-agency roll-ups", "Procurement paperwork and contract vehicles", "Data residency options", "Dedicated support", "Everything in Insights"] },
];

export function CheckoutDialog({ open, onOpenChange }) {
  const { user } = useAuth();
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState(user?.email || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const go = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: "claimed_profile_monthly", origin_url: window.location.origin, company, contact_email: email, user_id: user?.id || null,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="checkout-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl" style={{ fontWeight: 800 }}>Claim your company profile</DialogTitle>
          <DialogDescription>$149/month, cancel anytime. You'll be taken to Stripe's secure checkout.</DialogDescription>
        </DialogHeader>
        <form onSubmit={go} className="space-y-4">
          <div>
            <Label>Company on my360boss</Label>
            <div className="mt-1"><CompanyCombobox value={company} onChange={setCompany} /></div>
          </div>
          <div>
            <Label htmlFor="co-email">Billing / contact email</Label>
            <Input id="co-email" data-testid="checkout-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" />
          </div>
          {error && <p data-testid="checkout-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{error}</p>}
          <Button data-testid="checkout-submit" type="submit" disabled={!company || !email || busy} className="w-full h-11 bg-slate-900 hover:bg-emerald-600 rounded-full gap-2">
            <Lock size={15} /> {busy ? "Redirecting to Stripe..." : "Continue to secure checkout"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Pricing() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [checkout, setCheckout] = useState(false);

  return (
    <div data-testid="pricing-page">
      <section className="bg-slate-900 text-white lb-grain">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-20 text-left">
          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-500 text-slate-900 px-4 py-1.5 text-sm font-bold mb-6" data-testid="free-forever-banner">
            <ShieldCheck size={16} /> Free for employees. Forever.
          </div>
          <h1 className="font-display text-5xl sm:text-6xl leading-[0.95] tracking-tight max-w-3xl" style={{ fontWeight: 900 }}>
            Employees never pay.<br />Organizations pay for <span className="text-emerald-400">insight</span>.
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">
            Reading, reviewing, checking in, and the Documentation Vault will always be free — that's what keeps the data honest.
            Companies and agencies pay only for the cross-cutting analysis that helps them fix management before it costs them.
          </p>
          {params.get("cancelled") && <p data-testid="checkout-cancelled" className="mt-4 text-sm text-amber-300">Checkout cancelled — no charge was made.</p>}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 -mt-8 pb-8 grid md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="pricing-tiers">
        {TIERS.map((t) => (
          <div key={t.key} data-testid={`tier-${t.key}`} className={`rounded-2xl p-6 flex flex-col border ${t.highlight ? "bg-white border-emerald-500 shadow-xl shadow-emerald-500/10 ring-1 ring-emerald-500" : "bg-white border-slate-200"}`}>
            {t.highlight && <span className="self-start text-[11px] font-mono uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5 mb-3 flex items-center gap-1"><Sparkles size={11} /> Self-serve</span>}
            <p className="font-mono text-xs uppercase tracking-widest text-slate-500">{t.who}</p>
            <h2 className="font-display text-2xl text-slate-900 mt-1" style={{ fontWeight: 800 }}>{t.name}</h2>
            <div className="mt-3 flex items-baseline gap-1">
              <span className="font-display text-4xl text-slate-900" style={{ fontWeight: 900 }}>{t.price}</span>
              <span className="text-slate-500 text-sm">{t.period}</span>
            </div>
            {t.note && <p className="text-xs text-slate-400 mt-0.5">{t.note}</p>}
            <ul className="mt-5 space-y-2 text-sm text-slate-600 flex-1">
              {t.features.map((f) => <li key={f} className="flex gap-2"><Check size={16} className="text-emerald-600 shrink-0 mt-0.5" /> {f}</li>)}
            </ul>
            {t.checkout ? (
              <Button data-testid="tier-claimed-cta" onClick={() => setCheckout(true)} className="mt-6 w-full rounded-full bg-slate-900 hover:bg-emerald-600 gap-2">{t.cta} <ArrowRight size={15} /></Button>
            ) : (
              <Button data-testid={`tier-${t.key}-cta`} variant="outline" onClick={() => navigate(t.to)} className="mt-6 w-full rounded-full">{t.cta}</Button>
            )}
          </div>
        ))}
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-7 sm:p-9 grid lg:grid-cols-3 gap-8 items-center" data-testid="gov-pilot">
          <div className="lg:col-span-2">
            <p className="font-mono text-xs uppercase tracking-widest text-emerald-600 mb-2 flex items-center gap-1.5"><Landmark size={13} /> Government & public sector</p>
            <h2 className="font-display text-3xl sm:text-4xl text-slate-900" style={{ fontWeight: 800 }}>Agency Pilot — <span className="text-emerald-700">$9,500</span> / year</h2>
            <p className="text-slate-600 mt-3 leading-relaxed">
              Everything in Insights for a single agency, division, or facility. Priced under the federal micro-purchase threshold so a director can start
              without a procurement cycle. Includes Section 508-conscious UI, US data hosting, and a quarterly leadership report your IG and union will both respect.
            </p>
            <ul className="mt-4 grid sm:grid-cols-2 gap-2 text-sm text-slate-600">
              {["Up to 1,000 employees", "Open to Feedback & retaliation-risk trends", "Anonymity threshold on every slice", "W-9, SAM.gov registration, reseller options"].map((f) => <li key={f} className="flex gap-2"><Check size={16} className="text-emerald-600 shrink-0 mt-0.5" /> {f}</li>)}
            </ul>
          </div>
          <div className="flex flex-col gap-3">
            <Button data-testid="gov-pilot-cta" onClick={() => navigate("/for-employers#demo-form")} className="h-12 rounded-full bg-slate-900 hover:bg-emerald-600 gap-2"><Building2 size={16} /> Start a pilot conversation</Button>
            <p className="text-xs text-slate-400 text-center">Or email <a className="underline" href="mailto:founder@my360boss.com">founder@my360boss.com</a></p>
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 sm:px-8 pb-20">
        <h2 className="font-display text-3xl text-slate-900 mb-6" style={{ fontWeight: 800 }}>Pricing questions</h2>
        <div className="space-y-5 text-slate-600 leading-relaxed">
          <div><h3 className="font-semibold text-slate-900">Will employees ever be charged?</h3><p>No. Not for reading, reviewing, check-ins, or the Documentation Vault. If it's for an individual worker, it's free.</p></div>
          <div><h3 className="font-semibold text-slate-900">Does paying let a company see who wrote a review?</h3><p>Never. Paid tiers only ever receive aggregates, and every aggregate enforces the same 5-review minimum employees see. There is no filter or export that can shrink a group below the threshold.</p></div>
          <div><h3 className="font-semibold text-slate-900">Can a company remove reviews by paying?</h3><p>No. Moderation follows the same published rules for everyone. Claimed Profile lets you respond, not delete.</p></div>
          <div><h3 className="font-semibold text-slate-900">Why per-employee pricing for Insights?</h3><p>It's the standard HR-tech unit, so you can compare us directly with engagement-survey tools — and we cost less because your employees are already providing the signal.</p></div>
        </div>
        <p className="text-sm text-slate-400 mt-8">Want the full employer pitch? <Link to="/for-employers" className="underline text-emerald-700">See For Employers</Link>.</p>
      </section>

      <CheckoutDialog open={checkout} onOpenChange={setCheckout} />
    </div>
  );
}
